// Stock Ledger screen — append-only feed of every stock movement.
// Modeled on CycleCountTable.tsx: shared DataTable, moment for dates, a
// StatusBadge-style chip per transaction type.
import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { DataTable, DataTablePagination } from "../../shared/data-table";
import { StatusBadge } from "../../shared/status-badge";
import type { BadgeProps } from "ui/badge";

type Variant = NonNullable<BadgeProps["variant"]>;

const OPERATION_LABELS: Record<string, string> = {
  PURCHASE: "PURCHASE",
  SALE_CONSUMPTION: "SALE CONSUMPTION",
  SALES_RETURN: "SALES RETURN",
  ADMIN_STOCK_ADD: "STOCK",
  ADMIN_STOCK_REMOVE: "STOCK",
  // Combo stock movements (ComboStockService on the API).
  COMBO_BUILD: "COMBO BUILD",
  COMBO_ALLOCATION: "USED IN COMBO",
  COMBO_RELEASE: "COMBO RELEASE",
  COMBO_RETURN_BREAKUP: "COMBO RETURN",
  COMBO_RETURN_BREAKUP_REVERSAL: "COMBO RETURN UNDONE",
};

const OPERATION_VARIANTS: Record<string, Variant> = {
  PURCHASE: "success",
  SALE_CONSUMPTION: "destructive",
  SALES_RETURN: "warning",
  ADMIN_STOCK_ADD: "accent",
  ADMIN_STOCK_REMOVE: "accent",
  COMBO_BUILD: "success",
  COMBO_ALLOCATION: "warning",
  COMBO_RELEASE: "secondary",
  COMBO_RETURN_BREAKUP: "secondary",
  COMBO_RETURN_BREAKUP_REVERSAL: "warning",
};

export interface StockHistoryTableProps {
  data: any[];
  loading: boolean;
  title?: React.ReactNode;
  description?: React.ReactNode;
  headerActions?: React.ReactNode;
  toolbar?: React.ReactNode;
  searchValue?: string;
  onSearch?: (query: string) => void;
  searchPlaceholder?: string;
  pagination?: DataTablePagination;
}

export function StockHistoryTable({
  data,
  loading,
  title,
  description,
  headerActions,
  toolbar,
  searchValue,
  onSearch,
  searchPlaceholder,
  pagination,
}: StockHistoryTableProps) {
  const navigate = useNavigate();
  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        id: "ledgerCode",
        header: "ID",
        cell: ({ row }) => <span className="whitespace-nowrap font-medium">{row.original?.ledgerCode || "-"}</span>,
      },
      {
        id: "productId",
        header: "Product ID",
        cell: ({ row }) => {
          const productId = row.original?.productId;
          if (!productId) return <span className="block max-w-[140px] truncate">—</span>;
          return (
            <button
              type="button"
              className="max-w-[140px] truncate text-left text-primary underline-offset-2 hover:underline"
              title={`View ${productId} in Stock`}
              onClick={() => navigate(`/inventoryManagement?productId=${encodeURIComponent(productId)}`)}
            >
              {productId}
            </button>
          );
        },
      },
      {
        id: "createdOn",
        header: "When",
        cell: ({ row }) => {
          const record = row.original;
          return (
            <div className="whitespace-nowrap">
              <span className="block text-xs font-medium text-foreground">{record?.createdByName || "—"}</span>
              <span className="block text-muted-foreground">
                {record?.createdOn ? moment(record.createdOn).format("DD-MM-YY, hh:mm A") : "-"}
              </span>
            </div>
          );
        },
      },
      {
        id: "productName",
        header: "Name",
        cell: ({ row }) => (
          <span className="block max-w-[220px] truncate font-medium">{row.original?.productName}</span>
        ),
      },
      {
        id: "category",
        header: "Category",
        cell: ({ row }) => (
          <div
            className="max-w-[180px]"
            title={`Core Category: ${row.original?.coreCategoryName}\nType & Sub Category: ${row.original?.categoryName} - ${row.original?.subCategoryName}`}
          >
            <span className="block truncate">{row.original?.coreCategoryName || "—"}</span>
            <span className="block truncate text-muted-foreground">
              {row.original?.categoryName || "—"} - {row.original?.subCategoryName || "—"}
            </span>
          </div>
        ),
      },
      {
        id: "operationType",
        header: "Type",
        cell: ({ row }) => {
          const record = row.original;
          return (
            <StatusBadge
              status={OPERATION_LABELS[record?.operationType] ?? record?.operationType}
              variant={OPERATION_VARIANTS[record?.operationType]}
            />
          );
        },
      },
      {
        id: "quantity",
        header: () => <div className="text-right">Qty</div>,
        cell: ({ row }) => {
          const qty = row.original?.quantity;
          return (
            <div className={`text-right tabular-nums ${qty >= 0 ? "text-emerald-600" : "text-destructive"}`}>
              {qty > 0 ? `+${qty}` : qty}
            </div>
          );
        },
      },
      {
        id: "remarks",
        header: "Remarks",
        cell: ({ row }) => (
          <span className="block max-w-[280px] truncate text-muted-foreground">{row.original?.remarks || "—"}</span>
        ),
      },
    ],
    []
  );

  return (
    <DataTable
      columns={columns}
      data={data || []}
      loading={loading}
      title={title}
      description={description}
      headerActions={headerActions}
      toolbar={toolbar}
      searchable={!!onSearch}
      searchValue={searchValue}
      onSearch={onSearch}
      searchPlaceholder={searchPlaceholder}
      pagination={pagination}
    />
  );
}

export default StockHistoryTable;
